import React from 'react';
import type { CSSProperties, Component, HTMLAttributes } from 'react';
import type { CustomTransitionProps } from './types';
import type { TransitionStatus } from 'react-transition-group';
import type { TransitionProps } from 'react-transition-group/Transition';
import {
  DEFAULT_TRANSITION_DURATION,
  DEFAULT_TRANSITION_FUNC,
  LONG_TRANSITION_DURATION,
  SLOW_TRANSITION_DURATION, getAnimationDurationAsInt,
} from '../../util/styled';
import { Transition } from 'react-transition-group';

type Ref = HTMLDivElement;
type HTMLProps = HTMLAttributes<Ref>;
interface Props extends CustomTransitionProps, HTMLProps, Partial<Component<TransitionProps>> {
  /**
   * Position axis to move.
   */
  direction: 'top' | 'bottom' | 'left' | 'right',
  /**
   * Start position value set in %.
   */
  from: string,
  /**
   * End position value set in %.
   */
  to: string,
  /**
   * Additional default styles.
   */
  additionalStyle?: CSSProperties,
}

const Move = ({ direction, from, to, when, additionalStyle = {}, children, ...other }: Props): JSX.Element => {
  const default_duration = getAnimationDurationAsInt(LONG_TRANSITION_DURATION);
  const default_style: CSSProperties = {
    transition: `${direction} ${LONG_TRANSITION_DURATION} ${DEFAULT_TRANSITION_FUNC}, 
    opacity ${DEFAULT_TRANSITION_DURATION} ${DEFAULT_TRANSITION_FUNC} ${SLOW_TRANSITION_DURATION}`,
    position: 'absolute',
    opacity: 0,
    [direction]: from,
    ...additionalStyle,
  };

  const transition_styles: Record<TransitionStatus, CSSProperties> = {
    entering: {
      [direction]: from,
      opacity: 0,
    },
    entered: {
      [direction]: to,
      opacity: 1,
    },
    exiting: {
      [direction]: `calc(${to} / 2)`,
      opacity: '0.5',
    },
    exited: {
      [direction]: from,
      opacity: 0,
    },
    unmounted: {},
  };

  return (
    <Transition
      in={when}
      mountOnEnter
      unmountOnExit
      timeout={{ appear: 0, enter: 0, exit: default_duration }}
      {...(other as HTMLProps & Component<TransitionProps>)}
    >
      {(state) => (
        <div
          style={{
            ...default_style,
            ...transition_styles[state],
          }}
        >
          {children}
        </div>
      )}
    </Transition>
  );
};

Move.displayName = 'Move';

export default Move;
